'use client';

import { useEffect, useMemo, useState } from 'react';
import { Wallet, BarChart2, ChevronDown, History, Key, ShieldCheck, Gift, Activity } from 'lucide-react';
import { WalletView, type WalletTab } from '@/components/wallet/wallet-view';
import { AnalyticsView } from '@/components/analytics/analytics-view';
import { RunsTab } from '@/components/workspace/panels/recent-tabs';
import { useAuth } from '@/components/auth-provider';
import { useTranslation } from '@/components/i18n-provider';
import { cn } from '@/lib/utils';

export type AccountSection = 'wallet' | 'analytics' | 'runs';

/**
 * What `?section=` may name. `AccountSection` is the BODY kind (which view
 * renders); the extra members are the flat nav's own item ids, so a deep link
 * can land on Activity or Referrals rather than always on Balance. `/referrals`
 * is a real URL people are given — it has to open Referrals.
 */
export type AccountDeepLink =
  | AccountSection
  | 'balance'
  | 'activity'
  | 'spending'
  | 'referrals'
  | 'tokens';

/**
 * Account dialog — collapses the legacy Wallet and Analytics
 * surfaces into a single full-screen workspace dialog with a left
 * sidebar nav (mirrors the visual pattern of
 * [components/settings/settings-view.tsx](components/settings/settings-view.tsx)
 * so users get a consistent mental model).
 *
 * Section selection survives reload + deep-link via the `?section=`
 * query param parsed by the registry entry in
 * [components/workspace/fullscreen-nav.tsx](components/workspace/fullscreen-nav.tsx).
 *
 * Admin is NOT here. It is its own dialog reached from the user menu
 * (`/admin` in the fullscreen-nav registry): a staff surface does not
 * belong behind a nav of personal-account concerns, and nesting it made
 * AdminView render a second-level nav inside this one.
 *
 * Note on data: each child view (`WalletView`, `AnalyticsView`)
 * owns its own fetches and unmounts when its tab is
 * inactive. We don't try to share state across them — that would
 * couple three independent product areas through this shell.
 */
export function AccountView({
  embedded = false,
  section,
}: {
  embedded?: boolean;
  section?: AccountDeepLink;
} = {}) {
  const { user } = useAuth();
  /** The token/lock inspector stays reachable for support, not for users. */
  const isAdmin = user?.role === 'admin';
  const { t } = useTranslation();

  // SINGLE-level nav. The wallet sub-tabs (Balance / Activity / Tokens /
  // Spending / Referrals) are hoisted to sit as siblings of Analytics, so the
  // dialog has ONE flat menu instead of the old outer (Wallet/Analytics) +
  // inner (wallet tabs) two-column nest.
  type AccountItem = {
    id: string;
    label: string;
    icon: typeof Wallet;
    kind: AccountSection;
    /** Sub-tab to drive `WalletView` with, for `kind === 'wallet'`. */
    wallet?: WalletTab;
  };
  const items = useMemo<AccountItem[]>(() => {
    const list: AccountItem[] = [
      { id: 'balance', label: t('wallet.tabBalance'), icon: Wallet, kind: 'wallet', wallet: 'overview' },
      { id: 'activity', label: t('wallet.tabActivity'), icon: History, kind: 'wallet', wallet: 'activity' },
      { id: 'spending', label: t('wallet.tabSpending'), icon: ShieldCheck, kind: 'wallet', wallet: 'spending' },
      { id: 'referrals', label: t('wallet.tabReferrals'), icon: Gift, kind: 'wallet', wallet: 'referrals' },
      { id: 'analytics', label: t('nav.analytics'), icon: BarChart2, kind: 'analytics' },
      { id: 'runs', label: 'Runs', icon: Activity, kind: 'runs' },
    ];
    // Payment Tokens is a read-only inspector over the escrow layer (token
    // uuids, raw statuses, per-agent locks) with no user-facing control, so
    // it is support tooling, not a wallet section (2026-08-09). Users get the
    // money fact instead: "on hold" on the balance card, and a per-agent
    // "Work in progress" section in Runs.
    if (isAdmin) {
      list.splice(2, 0, { id: 'tokens', label: t('wallet.tabTokens'), icon: Key, kind: 'wallet', wallet: 'tokens' });
    }
    // Admin is NOT listed here; it is its own dialog reached from the user menu.
    return list;
  }, [t, isAdmin]);

  // Map the deep-link `?section=` to a concrete item id. Anything we do not
  // recognise (including the retired `?section=admin`) falls back to Balance.
  const initialId = useMemo<string>(() => {
    if (!section) return 'balance';
    // `wallet` is the legacy body-kind name for the wallet group; its landing
    // tab is Balance. Everything else is already an item id (and `activeItem`
    // below falls back to the first item if the id is not in this user's
    // list — `tokens` is admin-only).
    return section === 'wallet' ? 'balance' : section;
  }, [section]);

  const [activeId, setActiveId] = useState<string>(initialId);
  const [navOpen, setNavOpen] = useState(false);

  // Re-sync when the registry remounts us with a fresh `section` prop.
  useEffect(() => {
    if (!section) return;
    setActiveId(section === 'wallet' ? 'balance' : section);
  }, [section]);

  const activeItem = items.find((it) => it.id === activeId) ?? items[0];

  return (
    <div className={embedded ? 'w-full min-w-0' : 'w-full max-w-5xl mx-auto min-w-0'}>
      <div className="flex flex-col @lg/main:flex-row gap-6">
        {/* Sidebar — full nav on desktop, dropdown overlay on mobile.
            Matches the settings-view sidebar so the two dialogs read
            as the same shell. */}
        <nav className="@lg/main:w-48 shrink-0 relative">
          {(() => {
            const ActiveIcon = activeItem.icon;
            return (
              <button
                type="button"
                onClick={() => setNavOpen((o) => !o)}
                className="@lg/main:hidden w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold border border-border bg-card shadow-sm"
              >
                <span className="flex items-center gap-2">
                  <ActiveIcon className="h-4 w-4" />
                  {activeItem.label}
                </span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 transition-transform',
                    navOpen && 'rotate-180',
                  )}
                />
              </button>
            );
          })()}

          <div
            className={cn(
              navOpen ? 'block' : 'hidden',
              '@lg/main:block @lg/main:relative @lg/main:mt-0 @lg/main:shadow-none @lg/main:border-0 @lg/main:bg-transparent @lg/main:rounded-none @lg/main:max-h-none',
              'absolute left-0 right-0 mt-1 z-30 bg-popover border border-border rounded-lg shadow-lg max-h-[60vh] overflow-y-auto space-y-0.5 p-1 @lg/main:p-0',
            )}
          >
            {items.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setActiveId(id);
                  setNavOpen(false);
                }}
                data-testid={`account-tab-${id}`}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  activeId === id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </nav>

        {/* Section body. Each child view is mounted only when its
            tab is active so we don't pay the data-fetch cost for
            tabs the user never opens. */}
        <div className="flex-1 min-w-0 overflow-hidden">
          {activeItem.kind === 'wallet' && (
            <WalletView
              embedded
              hideNav
              activeTab={activeItem.wallet}
              onTabChange={(wt) => {
                const it = items.find((i) => i.kind === 'wallet' && i.wallet === wt);
                if (it) setActiveId(it.id);
              }}
            />
          )}
          {activeItem.kind === 'analytics' && <AnalyticsView embedded />}
          {activeItem.kind === 'runs' && (
            <div className="h-[min(640px,70vh)]">
              <RunsTab />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
